import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  LifeBuoy,
  ClipboardList,
  Mail,
  Copy,
  Check,
  ExternalLink,
  Send,
  Maximize2,
  X,
} from 'lucide-react';
import { callGetPlanCatalog } from '../../services/functions';
import { callAdminSendHelpDeskEmail } from '../../services/admin';
import {
  FALLBACK_PRICING,
  PROJECT_HELP,
  ProductId,
  Plan,
} from '../../data/helpDeskContent';

type CatalogPlan = {
  id: string;
  name: string;
  description?: string;
  price?: number;
  order?: number;
  active?: boolean;
  features?: unknown;
  limits?: unknown;
  tags?: unknown;
};

const BIOAXIS_LOGO_URL = 'https://bioaxis.co.za/bioaxis-logo-white.png';
const BIOAXIS_SITE_URL = 'https://bioaxis.co.za/';

type PreviewMode = 'rendered' | 'text' | 'html';

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatCurrency = (amount: number) => `R${amount.toLocaleString('en-ZA')}/month`;

const LABEL_MAP: Record<string, string> = {
  users_included: 'Users',
  usersIncluded: 'Users',
  additionalUserCost: 'Additional user cost',
  maxUsers: 'Max users',
  animals: 'Animals',
  clients: 'Clients',
  invoicesPerMonth: 'Invoices/month',
  clinicalJobsPerMonth: 'Clinical Jobs/month',
  transcripts: 'Transcripts/month',
  templates: 'Templates',
  library_categories: 'Library categories',
  libraryCategories: 'Library categories',
};

const titleCase = (raw: string) => raw
  .replace(/[_-]/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (c) => c.toUpperCase());

const toLabel = (key: string) => LABEL_MAP[key] || titleCase(key);

const normalizeTags = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string').map((item) => item.toLowerCase());
};

const normalizeFeatures = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== null && v !== false)
      .map(([k, v]) => {
        if (v === true) return toLabel(k);
        return `${toLabel(k)}: ${String(v)}`;
      });
  }
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const formatLimitValue = (key: string, value: unknown) => {
  if (key === 'additionalUserCost' && typeof value === 'number') return `+R${value} per additional user`;
  return String(value);
};

const normalizeLimits = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== null && v !== false)
      .map(([k, v]) => `${toLabel(k)}: ${formatLimitValue(k, v)}`);
  }
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const toPlan = (raw: CatalogPlan): Plan => ({
  id: raw.id,
  name: raw.name || raw.id,
  price: Number(raw.price || 0),
  description: raw.description || '',
  features: normalizeFeatures(raw.features),
  limits: normalizeLimits(raw.limits),
  tags: normalizeTags(raw.tags),
});

const toSendErrorMessage = (error: any): string => {
  const code = String(error?.code || '').replace('functions/', '');
  const message = String(error?.message || '').trim();

  if (code === 'failed-precondition') {
    return 'Email sending is not configured yet. Please verify SMTP credentials and sender settings.';
  }
  if (code === 'permission-denied') {
    return 'Admin access is required to send emails from Help Desk.';
  }
  if (code === 'unauthenticated') {
    return 'Please sign in again, then retry sending.';
  }
  if (code === 'internal' || message.toLowerCase() === 'internal') {
    return 'Email provider returned an internal error. Check provider/API key configuration and retry.';
  }
  if (message) {
    return message;
  }
  return 'Failed to send email. Check backend email configuration.';
};

const AdminHelpDesk: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<ProductId>('vetaxis');
  const [catalog, setCatalog] = useState<Partial<Record<ProductId, Plan[]>>>({});
  const [plansLoading, setPlansLoading] = useState(true);
  const [copied, setCopied] = useState<null | 'html' | 'text'>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('rendered');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const [recipientName, setRecipientName] = useState('');
  const [organization, setOrganization] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('BioAxis Team');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendStatus, setSendStatus] = useState<null | { type: 'success' | 'error'; message: string }>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const response = await callGetPlanCatalog();
        const normalize = (plans: CatalogPlan[]) => plans
          .filter((plan) => plan.active !== false)
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
          .map(toPlan)
          .map((plan) => ({
            ...plan,
            features: plan.features.length > 0 ? plan.features : ['Core features included'],
            limits: plan.limits.length > 0 ? plan.limits : ['Usage limits based on selected plan'],
          }));

        if (mounted) {
          setCatalog({
            vetaxis: normalize(response.vetaxis as CatalogPlan[]),
            staffaxis: normalize(response.staffaxis as CatalogPlan[]),
            noteaxis: normalize(response.noteaxis as CatalogPlan[]),
          });
        }
      } catch {
        // Fallback pricing is used when plan catalog is unavailable.
      } finally {
        if (mounted) setPlansLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const project = PROJECT_HELP[selectedProduct];
  const plans = useMemo(() => {
    const remotePlans = catalog[selectedProduct];
    if (remotePlans && remotePlans.length > 0) {
      return remotePlans;
    }
    return FALLBACK_PRICING[selectedProduct];
  }, [catalog, selectedProduct]);

  const plainTextEmail = useMemo(() => {
    const greeting = recipientName.trim() ? `Hi ${recipientName.trim()},` : 'Hi there,';
    const orgText = organization.trim() ? `for ${organization.trim()}` : 'for your team';

    const summaryFeatures = project.keyFeatures.map((feature) => `- ${feature}`).join('\n');
    const planComparison = plans.map((plan) => [
      `${plan.name} — ${formatCurrency(plan.price)}`,
      plan.description ? `  ${plan.description}` : '',
      '  Features:',
      ...plan.features.map((feature) => `  - ${feature}`),
      '  Limits:',
      ...plan.limits.map((limit) => `  - ${limit}`),
    ].filter(Boolean).join('\n')).join('\n\n');

    return [
      greeting,
      '',
      `Here is a ${project.name} proposal ${orgText}.`,
      '',
      `${project.name} | ${project.tagline}`,
      project.summary,
      '',
      'What it offers:',
      summaryFeatures,
      '',
      'Price comparison:',
      planComparison,
      '',
      `Visit our site to see more details: ${BIOAXIS_SITE_URL}`,
      'Reply to this email and we can schedule a live demo.',
      '',
      `Regards,\n${senderName.trim() || 'BioAxis Team'}`,
    ].join('\n');
  }, [organization, plans, project, recipientName, senderName]);

  const htmlEmail = useMemo(() => {
    const safeGreeting = escapeHtml(recipientName.trim() ? `Hi ${recipientName.trim()},` : 'Hi there,');
    const safeOrg = escapeHtml(organization.trim() || 'your team');
    const safeSender = escapeHtml(senderName.trim() || 'BioAxis Team');

    const featuresHtml = project.keyFeatures
      .map((feature) => `<li style="margin:0 0 8px 0;color:#1f2937;line-height:1.5;">${escapeHtml(feature)}</li>`)
      .join('');

    const planCards = plans.map((plan, index) => {
      const isPopular = (plan.tags || []).includes('popular');
      const featureList = plan.features
        .map((feature) => `<li style="margin:0 0 7px 0;color:#374151;font-size:13px;line-height:1.4;">✓ ${escapeHtml(feature)}</li>`)
        .join('');
      const limitsList = plan.limits
        .map((limit) => `<li style="margin:0 0 7px 0;color:#374151;font-size:13px;line-height:1.4;">✓ ${escapeHtml(limit)}</li>`)
        .join('');

      return `
        <td valign="top" style="padding:${index === 0 ? '0 8px 0 0' : index === plans.length - 1 ? '0 0 0 8px' : '0 8px'};">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid ${isPopular ? '#141b2b' : '#d1d5db'};border-radius:14px;overflow:hidden;background:#ffffff;">
            ${isPopular ? `<tr><td style="padding:8px 14px;background:#1a2436;color:#ffffff;text-align:center;font-size:11px;font-weight:700;letter-spacing:.3px;text-transform:uppercase;">Most Popular</td></tr>` : ''}
            <tr>
              <td style="padding:16px;">
                <p style="margin:0;color:#111827;font-size:22px;font-weight:800;">${escapeHtml(plan.name)}</p>
                <p style="margin:6px 0 0 0;color:#6b7280;font-size:13px;">${escapeHtml(plan.description)}</p>
                <p style="margin:16px 0 14px 0;color:#111827;font-size:38px;font-weight:800;line-height:1;">R${Number(plan.price).toLocaleString('en-ZA')}<span style="font-size:15px;color:#475569;font-weight:500;">/month</span></p>

                <p style="margin:0 0 8px 0;font-size:11px;font-weight:700;letter-spacing:.6px;color:#64748b;text-transform:uppercase;">Features</p>
                <ul style="margin:0 0 14px 18px;padding:0;">${featureList}</ul>

                <p style="margin:0 0 8px 0;font-size:11px;font-weight:700;letter-spacing:.6px;color:#64748b;text-transform:uppercase;">Limits</p>
                <ul style="margin:0 0 12px 18px;padding:0;">${limitsList}</ul>

                <a href="https://bioaxis.co.za/plans" style="display:block;text-align:center;margin-top:14px;padding:10px 14px;border-radius:10px;background:${isPopular ? '#1a2436' : '#ffffff'};border:1px solid #cbd5e1;color:${isPopular ? '#ffffff' : '#0f172a'};font-weight:700;text-decoration:none;font-size:13px;">Subscribe Now</a>
              </td>
            </tr>
          </table>
        </td>
      `;
    }).join('');

    return `
<!DOCTYPE html>
<html>
  <body style="margin:0;padding:0;background:#f8fafc;font-family:Nunito,Arial,sans-serif;color:#0f172a;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="padding:22px 14px;">
      <tr>
        <td align="center">
          <table role="presentation" width="980" cellspacing="0" cellpadding="0" style="max-width:980px;background:#ffffff;border:1px solid #dbe4ef;border-radius:18px;overflow:hidden;">
            <tr>
              <td style="padding:24px;background:linear-gradient(135deg,#1a2436,#111825);text-align:center;">
                <img src="${BIOAXIS_LOGO_URL}" alt="BioAxis logo" style="max-width:200px;height:auto;display:block;margin:0 auto 10px auto;" />
                <p style="margin:0;color:#e6eaef;font-size:12px;letter-spacing:1px;text-transform:uppercase;">BioAxis Product Proposal</p>
              </td>
            </tr>
            <tr>
              <td style="padding:24px;">
                <p style="margin:0 0 12px 0;font-size:16px;color:#0f172a;">${safeGreeting}</p>
                <p style="margin:0 0 18px 0;font-size:15px;color:#334155;line-height:1.6;">Here is a ${escapeHtml(project.name)} proposal for ${safeOrg}, including key features and pricing comparison.</p>

                <h2 style="margin:0 0 8px 0;font-size:24px;color:#0f172a;">${escapeHtml(project.name)} <span style="color:#1a2436;">| ${escapeHtml(project.tagline)}</span></h2>
                <p style="margin:0 0 16px 0;font-size:14px;color:#334155;line-height:1.7;">${escapeHtml(project.summary)}</p>

                <p style="margin:0 0 8px 0;color:#1a2436;font-size:16px;font-weight:700;">What It Offers</p>
                <ul style="margin:0 0 18px 18px;padding:0;">${featuresHtml}</ul>

                <p style="margin:0 0 12px 0;color:#1a2436;font-size:16px;font-weight:700;">Price Comparison</p>
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse:collapse;margin:0 0 20px 0;">
                  <tr>
                    ${planCards}
                  </tr>
                </table>

                <p style="margin:0 0 18px 0;font-size:14px;color:#334155;">
                  Visit our site to see more details:
                  <a href="${BIOAXIS_SITE_URL}" style="color:#1a2436;margin-left:4px;">${BIOAXIS_SITE_URL}</a>
                </p>

                <div style="padding:14px;background:#f3f5f7;border:1px solid #dbe4ef;border-radius:10px;">
                  <p style="margin:0;font-size:13px;color:#1a2436;line-height:1.6;">Reply to this email and we can schedule a live demo.</p>
                </div>

                <p style="margin:16px 0 0 0;font-size:14px;color:#0f172a;">Regards,<br />${safeSender}</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>
    `.trim();
  }, [organization, plans, project, recipientName, senderName]);

  const copyContent = async (type: 'html' | 'text', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };

  const openDraft = () => {
    const target = recipientEmail.trim();
    const mailtoBase = target ? `mailto:${target}` : 'mailto:';
    const subject = encodeURIComponent(`${project.name} promotional proposal`);
    const body = encodeURIComponent(plainTextEmail);
    window.location.href = `${mailtoBase}?subject=${subject}&body=${body}`;
  };

  const sendEmail = async () => {
    const to = recipientEmail.trim();
    if (!to) {
      setSendStatus({ type: 'error', message: 'Recipient email is required for direct send.' });
      return;
    }

    setSendingEmail(true);
    setSendStatus(null);
    try {
      await callAdminSendHelpDeskEmail({
        to,
        subject: `${project.name} promotional proposal`,
        html: htmlEmail,
        text: plainTextEmail,
        projectId: selectedProduct,
        recipientName: recipientName.trim() || undefined,
        organization: organization.trim() || undefined,
      });
      setSendStatus({ type: 'success', message: `Email sent to ${to}.` });
    } catch (error: any) {
      setSendStatus({
        type: 'error',
        message: toSendErrorMessage(error),
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
                <LifeBuoy className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Client Help Desk</h1>
                <p className="text-slate-600 dark:text-gray-400">Onboarding flow and premium client-ready product proposal emails</p>
              </div>
            </div>
            <a
              href={project.productUrl}
              target="_blank"
              rel="noreferrer"
              className="glass-effect rounded-xl p-3 flex items-center gap-2 text-sm text-primary-700 dark:text-primary-300 hover:underline"
            >
              Open product site <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        <motion.div
          className="glass-effect rounded-2xl shadow-glass p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Client Onboarding Checklist</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {(['vetaxis', 'staffaxis', 'noteaxis'] as ProductId[]).map((productId) => {
              const item = PROJECT_HELP[productId];
              const active = selectedProduct === productId;
              return (
                <button
                  key={item.id}
                  onClick={() => setSelectedProduct(item.id)}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    active
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-gray-700 hover:border-primary-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.name}</p>
                  <p className="text-xs text-slate-600 dark:text-gray-400 mt-1">{item.tagline}</p>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-gray-800/50">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{project.name}</h3>
              <p className="text-sm text-slate-600 dark:text-gray-400 mt-1">{project.summary}</p>

              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">What it offers</p>
                <ul className="space-y-1.5">
                  {project.keyFeatures.map((feature) => (
                    <li key={feature} className="text-sm text-slate-700 dark:text-gray-300">• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-slate-200 dark:border-gray-700">
              <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Client onboarding checklist</p>
              <ol className="space-y-1.5 list-decimal pl-5">
                {project.onboardingChecklist.map((item) => (
                  <li key={item} className="text-sm text-slate-600 dark:text-gray-400">{item}</li>
                ))}
              </ol>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Price comparison</p>
            <p className="text-xs text-slate-600 dark:text-gray-400 mb-3">
              {plansLoading
                ? 'Loading plan catalog from Firebase Functions...'
                : (catalog[selectedProduct]?.length ? 'Live plan catalog' : 'Fallback plan catalog')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const isPopular = (plan.tags || []).includes('popular');
                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl border p-5 bg-white/85 dark:bg-gray-900/40 ${
                      isPopular
                        ? 'border-primary-500 shadow-md'
                        : 'border-slate-200 dark:border-gray-700'
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-2 right-4 px-3 py-1 rounded-full text-[11px] font-semibold bg-primary-600 text-white">
                        Most Popular
                      </span>
                    )}
                    <p className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</p>
                    <p className="text-sm text-slate-600 dark:text-gray-400 mt-1">{plan.description}</p>
                    <p className="text-4xl font-bold text-slate-900 dark:text-white mt-3">R{plan.price}<span className="text-sm font-medium text-slate-500 dark:text-gray-400">/month</span></p>

                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mt-4 mb-2">Features</p>
                    <ul className="space-y-1">
                      {plan.features.map((feature) => (
                        <li key={`${plan.id}-f-${feature}`} className="text-sm text-slate-700 dark:text-gray-300">✓ {feature}</li>
                      ))}
                    </ul>

                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mt-4 mb-2">Limits</p>
                    <ul className="space-y-1">
                      {plan.limits.map((limit) => (
                        <li key={`${plan.id}-l-${limit}`} className="text-sm text-slate-700 dark:text-gray-300">✓ {limit}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="glass-effect rounded-2xl shadow-glass p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Email Proposal Builder</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Recipient name</label>
                  <input
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Client name"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-700 bg-transparent text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Organization</label>
                  <input
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="Clinic or company"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-700 bg-transparent text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Recipient email</label>
                  <input
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="ops@client.com"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-700 bg-transparent text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-gray-400 mb-1">Sender signature</label>
                  <input
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="BioAxis Team"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-700 bg-transparent text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50/80 dark:bg-gray-800/40">
                <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Actions</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => copyContent('html', htmlEmail)}
                    className="btn-primary !px-4 !py-2 !rounded-xl text-sm flex items-center gap-2"
                  >
                    {copied === 'html' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    Copy HTML
                  </button>

                  <button
                    onClick={() => copyContent('text', plainTextEmail)}
                    className="btn-secondary !px-4 !py-2 !rounded-xl text-sm flex items-center gap-2"
                  >
                    {copied === 'text' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    Copy Text
                  </button>

                  <button
                    onClick={openDraft}
                    className="px-4 py-2 rounded-xl border border-slate-300 dark:border-gray-600 text-sm font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
                  >
                    Open Draft
                  </button>

                  <button
                    onClick={sendEmail}
                    disabled={sendingEmail}
                    className={`px-4 py-2 rounded-xl text-sm font-medium text-white transition-all flex items-center gap-2 ${
                      sendingEmail ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'
                    }`}
                  >
                    {sendingEmail ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
                {sendStatus && (
                  <p className={`mt-3 text-xs ${
                    sendStatus.type === 'success'
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-red-700 dark:text-red-400'
                  }`}>
                    {sendStatus.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewMode('rendered')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${previewMode === 'rendered' ? 'bg-primary-600 text-white' : 'border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300'}`}
                >
                  HTML Preview
                </button>
                <button
                  onClick={() => setPreviewMode('text')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${previewMode === 'text' ? 'bg-primary-600 text-white' : 'border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300'}`}
                >
                  Text Preview
                </button>
                <button
                  onClick={() => setPreviewMode('html')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${previewMode === 'html' ? 'bg-primary-600 text-white' : 'border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300'}`}
                >
                  HTML Code
                </button>
                <button
                  onClick={() => setIsPreviewModalOpen(true)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 text-xs font-medium text-slate-700 dark:text-gray-300 flex items-center gap-1.5 hover:bg-slate-100 dark:hover:bg-gray-800"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  Pop-out
                </button>
              </div>

              {previewMode === 'rendered' && (
                <iframe
                  title="Email HTML preview"
                  srcDoc={htmlEmail}
                  className="w-full h-[560px] rounded-xl border border-slate-200 dark:border-gray-700 bg-white"
                />
              )}

              {previewMode === 'text' && (
                <pre className="p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 text-xs text-slate-700 dark:text-gray-300 whitespace-pre-wrap max-h-[560px] overflow-y-auto">
                  {plainTextEmail}
                </pre>
              )}

              {previewMode === 'html' && (
                <pre className="p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 text-[11px] text-slate-700 dark:text-gray-300 whitespace-pre-wrap max-h-[560px] overflow-y-auto">
                  {htmlEmail}
                </pre>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {isPreviewModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/65 backdrop-blur-sm p-3 sm:p-6">
          <div className="h-full max-w-6xl mx-auto rounded-2xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-gray-700">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Large Preview</p>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 text-xs font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
              >
                <X className="w-3.5 h-3.5" />
                Close
              </button>
            </div>

            <div className="p-4 sm:p-5 flex-1 overflow-y-auto">
              {previewMode === 'rendered' && (
                <iframe
                  title="Email HTML preview expanded"
                  srcDoc={htmlEmail}
                  className="w-full h-[80vh] rounded-xl border border-slate-200 dark:border-gray-700 bg-white"
                />
              )}

              {previewMode === 'text' && (
                <pre className="p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 text-xs text-slate-700 dark:text-gray-300 whitespace-pre-wrap max-h-[80vh] overflow-y-auto">
                  {plainTextEmail}
                </pre>
              )}

              {previewMode === 'html' && (
                <pre className="p-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/50 text-[11px] text-slate-700 dark:text-gray-300 whitespace-pre-wrap max-h-[80vh] overflow-y-auto">
                  {htmlEmail}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelpDesk;
